// src/features/auth/service/authService.js
import {
  auth,
  db,
  googleProvider,
  signInWithPopup,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification,
  onAuthStateChanged,
} from "../../../config/firebase";

class AuthService {
  // Register with email and password
  async register(email, password, userData) {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;

      // Update profile
      await updateProfile(user, {
        displayName: userData.name || "",
        photoURL: userData.photoURL || "",
      });

      // Send email verification
      await sendEmailVerification(user);

      // Save user data to Firestore
      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        email: user.email,
        name: userData.name || "",
        phone: userData.phone || "",
        role: userData.role || "photographer",
        company: userData.company || "Hashtag Photography Studio",
        emailVerified: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        settings: {
          theme: "light",
          language: "en",
          notifications: true,
        },
        permissions: userData.permissions || [
          "view_dashboard",
          "manage_gallery",
        ],
      });

      return {
        user: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          emailVerified: user.emailVerified,
        },
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Login with email and password
  async login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;

      // Get user data from Firestore
      const userDoc = await getDoc(doc(db, "users", user.uid));
      const userData = userDoc.exists() ? userDoc.data() : {};

      // Update last login
      await updateDoc(doc(db, "users", user.uid), {
        lastLogin: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return {
        user: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          emailVerified: user.emailVerified,
          photoURL: user.photoURL,
          ...userData,
        },
        token: await user.getIdToken(),
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Login with Google
  async loginWithGoogle() {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Check if user exists in Firestore
      const userDoc = await getDoc(doc(db, "users", user.uid));

      if (!userDoc.exists()) {
        // Create new user document
        await setDoc(doc(db, "users", user.uid), {
          uid: user.uid,
          email: user.email,
          name: user.displayName || "",
          photoURL: user.photoURL || "",
          role: "photographer",
          company: "Hashtag Photography Studio",
          emailVerified: user.emailVerified,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          settings: {
            theme: "light",
            language: "en",
            notifications: true,
          },
          permissions: ["view_dashboard", "manage_gallery"],
        });
      } else {
        // Update existing user
        await updateDoc(doc(db, "users", user.uid), {
          lastLogin: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      const userData = userDoc.exists() ? userDoc.data() : {};

      return {
        user: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          emailVerified: user.emailVerified,
          photoURL: user.photoURL,
          ...userData,
        },
        token: await user.getIdToken(),
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Logout
  async logout() {
    try {
      await signOut(auth);
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      return { success: true };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Send password reset email
  async resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true, message: "Password reset email sent" };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get current user
  async getCurrentUser() {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        unsubscribe();
        if (user) {
          try {
            const userDoc = await getDoc(doc(db, "users", user.uid));
            const userData = userDoc.exists() ? userDoc.data() : {};
            resolve({
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
              emailVerified: user.emailVerified,
              photoURL: user.photoURL,
              ...userData,
            });
          } catch (error) {
            reject(error);
          }
        } else {
          resolve(null);
        }
      });
    });
  }

  // Handle errors
  handleError(error) {
    let message = "An error occurred";

    switch (error.code) {
      case "auth/email-already-in-use":
        message = "This email is already registered";
        break;
      case "auth/invalid-email":
        message = "Invalid email address";
        break;
      case "auth/user-disabled":
        message = "This account has been disabled";
        break;
      case "auth/user-not-found":
        message = "No account found with this email";
        break;
      case "auth/wrong-password":
        message = "Incorrect password";
        break;
      case "auth/too-many-requests":
        message = "Too many attempts. Please try again later";
        break;
      case "auth/network-request-failed":
        message = "Network error. Please check your connection";
        break;
      case "auth/popup-closed-by-user":
        message = "Popup closed before completing sign in";
        break;
      default:
        message = error.message || "An unexpected error occurred";
    }

    return { code: error.code, message };
  }
}

const authService = new AuthService();
export default authService;
